import { Observable, Subject, of, throwError } from 'rxjs';
import { bufferTime, catchError, filter, map, retry, share, switchMap } from 'rxjs/operators';
import generateUniqueId from 'uuid/v4';

import { objectKeysToPascalCase } from './util/ObjectKeysToPascalCase';

import { DefaultVbrickLoggingClientConfig } from './DefaultVbrickLoggingClientConfig';
import { IMessageBatch } from './IMessageBatch';
import { IMessageContext } from './IMessageContext';
import { IMessageDispatcher } from './IMessageDispatcher';
import { IStandardEventMessageBody } from './IStandardEventMessageBody';
import { IVbrickLoggingClientConfig } from './IVbrickLoggingClientConfig';
import { LogLevel, meetsLoggingThreshold } from './LogLevel';
import { MessageType } from './MessageType';
import { TokenManager } from './TokenManager';

export const MAX_SEND_MESSAGES_RETRIES: number = 3;

export class VbrickLoggingClient {
	private messagesSubject$ = new Subject<any>();
	private tokenManager: TokenManager;

	protected logLevelThreshold: LogLevel;

	public logDispatches$: Observable<IMessageBatch>;
	public messages$ = this.messagesSubject$.asObservable();

	constructor(
		private context: IMessageContext,
		public messageDispatcher: IMessageDispatcher,
		private config: IVbrickLoggingClientConfig = DefaultVbrickLoggingClientConfig
	) {
		const messageBatches$: Observable<IMessageBatch> = this.messages$.pipe(
			bufferTime(this.config.bufferTimeSpanMs, null, config.maxBufferSize),
			filter(msgs => !!msgs.length),
			map(messages => this.mergeBodyWithContext(messages))
		);

		this.logLevelThreshold = config.logLevel || LogLevel.INFO;
		this.tokenManager = new TokenManager(messageDispatcher);

		this.logDispatches$ = messageBatches$.pipe(
			switchMap(messages =>
				of({}).pipe( // layer is needed for retry of the whole flow
					switchMap(() => this.tokenManager.getCurrentToken()),
					switchMap(token => this.messageDispatcher.sendMessages(messages, token)),
					catchError(error => this.checkTokenAndThrowError(error)),
					retry(MAX_SEND_MESSAGES_RETRIES),
					map(() => messages)
				)
			),
			share()
		);

		this.logDispatches$.subscribe(() => { /* noop */}, error => this.config.onError(error));
	}

	public debug(eventType: string, message: string, data?: any): void {
		this.logStandardEvent(LogLevel.DEBUG, eventType, message, data);
	}

	public destroy(): void {
		this.messagesSubject$.complete();
	}

	public error(eventType: string, message: string, data?: any, exception?: string): void {
		this.logStandardEvent(LogLevel.ERROR, eventType, message, data, exception);
	}

	public fatal(eventType: string, message: string, data?: any, exception?: string): void {
		this.logStandardEvent(LogLevel.FATAL, eventType, message, data, exception);
	}

	public info(eventType: string, message: string, data?: any): void {
		this.logStandardEvent(LogLevel.INFO, eventType, message, data);
	}

	public log(messageBody: any): void {
		this.messagesSubject$.next(messageBody);
	}

	public setContext(context: any): void {
		this.context = context;
	}

	public trace(eventType: string, message: string, data?: any): void {
		this.logStandardEvent(LogLevel.TRACE, eventType, message, data);
	}

	public warn(eventType: string, message: string, data?: any): void {
		this.logStandardEvent(LogLevel.WARNING, eventType, message, data);
	}

	protected logStandardEvent(level: LogLevel, eventType: string, message: string, data: any, exception?: string, ignoreThreshold?: boolean): void {
		// ignore those that do not meet the configured threshold
		if (!ignoreThreshold && !meetsLoggingThreshold(level, this.logLevelThreshold)) {
			return;
		}

		const standardLogMsg: IStandardEventMessageBody = {
			...data,
			eventType,
			exception,
			level,
			message,
			when: new Date()
		};

		this.log(standardLogMsg);
	}

	private checkTokenAndThrowError(error: any): Observable<never> {
		if (error) {
			if (error.invalidToken) {
				this.tokenManager.invalidateToken();
			} else {
				console.error('Failed to send messages', error);
			}
		}

		return throwError(error);
	}

	private mergeBodyWithContext(messageBodies: any[]): IMessageBatch {
		const merged = {
			accountId: this.context.accountId,
			data: messageBodies.map(msgBody => ({
				...msgBody,
				id: generateUniqueId(),
				type: this.context.subType
			})),
			hostname: this.context.hostname,
			ipAddress: this.context.ipAddress || (messageBodies[0] || {}).ipAddress,
			resourceId: this.context.resourceId,
			source: this.context.source,
			type: this.context.type
		};

		if (merged.type === MessageType.ANALYTICS) {
			merged.data = merged.data.map(entry => objectKeysToPascalCase(entry));
		}

		return merged;
	}
}
